import { useState, useMemo, useCallback } from "react";
import { EditField } from "../types";
import { evaluateCondition, formatElementName } from "../utils";

export interface CategoryItem {
  name: string;
  key: string;
  count: number;
}

export interface GroupedFields {
  baseFields: EditField[];
  groups: Record<string, EditField[]>;
}

export interface UseEditPanelFieldsResult {
  activeTab: "desktop" | "mobile";
  setActiveTab: (tab: "desktop" | "mobile") => void;
  hasResponsiveFields: boolean;

  searchQuery: string;
  setSearchQuery: (query: string) => void;
  activeCategory: string | null;
  setActiveCategory: (category: string | null) => void;

  categoryList: CategoryItem[];
  hasSidebar: boolean;
  hasSearchBar: boolean;

  displayedGroupedFields: GroupedFields;
  totalDisplayedFields: number;
  isShowingAllCategories: boolean;

  searchMatchCounts: Record<string, number> | null;

  collapsedGroups: Set<string>;
  toggleGroupCollapse: (groupName: string) => void;
}

export function useEditPanelFields(
  fields: EditField[],
  values: Record<string, any>,
): UseEditPanelFieldsResult {
  const [activeTab, setActiveTab] = useState<"desktop" | "mobile">("desktop");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  const hasResponsiveFields = useMemo(
    () => fields.some((f) => f.responsive),
    [fields],
  );

  const visibleFields = useMemo(() => {
    if (activeTab === "mobile" && hasResponsiveFields) {
      return fields
        .filter((f) => f.responsive)
        .map(
          (f): EditField => ({
            ...f,
            key: `mobile_${f.key}`,
            label: f.label,
          }),
        )
        .filter((f) => evaluateCondition(f.condition, values));
    }

    return fields
      .filter((f) => !f.key.startsWith("mobile_"))
      .filter((f) => evaluateCondition(f.condition, values));
  }, [fields, activeTab, hasResponsiveFields, values]);

  const groupedFields = useMemo((): GroupedFields => {
    const baseFields: EditField[] = [];
    const groups: Record<string, EditField[]> = {};

    visibleFields.forEach((field) => {
      if (field.group) {
        if (!groups[field.group]) {
          groups[field.group] = [];
        }
        groups[field.group].push(field);
      } else {
        const elementMatch = field.key.match(/^elementStyles\.([^.]+)\./);
        if (elementMatch) {
          const elementName = elementMatch[1];
          if (!groups[elementName]) {
            groups[elementName] = [];
          }
          groups[elementName].push(field);
        } else {
          baseFields.push(field);
        }
      }
    });

    return { baseFields, groups };
  }, [visibleFields]);

  const categoryList = useMemo((): CategoryItem[] => {
    const categories: CategoryItem[] = [];
    categories.push({
      name: "All",
      key: "__all__",
      count: visibleFields.length,
    });
    if (groupedFields.baseFields.length > 0) {
      categories.push({
        name: "General",
        key: "__general__",
        count: groupedFields.baseFields.length,
      });
    }
    Object.entries(groupedFields.groups).forEach(([groupName, gFields]) => {
      categories.push({
        name: formatElementName(groupName),
        key: groupName,
        count: gFields.length,
      });
    });
    return categories;
  }, [visibleFields, groupedFields]);

  const hasSidebar = categoryList.length > 3;
  const hasSearchBar = hasSidebar || visibleFields.length >= 8;

  const matchesSearch = useCallback(
    (field: EditField, query: string): boolean => {
      if (!query) return true;
      return (
        field.label.toLowerCase().includes(query) ||
        (field.group ? field.group.toLowerCase().includes(query) : false) ||
        field.key.toLowerCase().includes(query)
      );
    },
    [],
  );

  const displayedGroupedFields = useMemo((): GroupedFields => {
    const query = searchQuery.toLowerCase().trim();

    let baseFields = groupedFields.baseFields;
    let groups = { ...groupedFields.groups };

    if (query) {
      baseFields = baseFields.filter((f) => matchesSearch(f, query));
      const filteredGroups: Record<string, EditField[]> = {};
      Object.entries(groups).forEach(([name, gFields]) => {
        const filtered = gFields.filter((f) => matchesSearch(f, query));
        if (filtered.length > 0) {
          filteredGroups[name] = filtered;
        }
      });
      groups = filteredGroups;
    }

    if (activeCategory && activeCategory !== "__all__") {
      if (activeCategory === "__general__") {
        groups = {};
      } else {
        baseFields = [];
        groups = groups[activeCategory]
          ? { [activeCategory]: groups[activeCategory] }
          : {};
      }
    }

    return { baseFields, groups };
  }, [groupedFields, searchQuery, activeCategory, matchesSearch]);

  const searchMatchCounts = useMemo((): Record<string, number> | null => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return null;

    const counts: Record<string, number> = {};
    counts["__general__"] = groupedFields.baseFields.filter((f) =>
      matchesSearch(f, query),
    ).length;

    Object.entries(groupedFields.groups).forEach(([name, gFields]) => {
      counts[name] = gFields.filter((f) => matchesSearch(f, query)).length;
    });

    counts["__all__"] = Object.values(counts).reduce((a, b) => a + b, 0);
    return counts;
  }, [searchQuery, groupedFields, matchesSearch]);

  const totalDisplayedFields =
    displayedGroupedFields.baseFields.length +
    Object.values(displayedGroupedFields.groups).reduce(
      (sum, g) => sum + g.length,
      0,
    );

  const isShowingAllCategories =
    !activeCategory || activeCategory === "__all__";

  const toggleGroupCollapse = useCallback((groupName: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  }, []);

  return {
    activeTab,
    setActiveTab,
    hasResponsiveFields,
    searchQuery,
    setSearchQuery,
    activeCategory,
    setActiveCategory,
    categoryList,
    hasSidebar,
    hasSearchBar,
    displayedGroupedFields,
    totalDisplayedFields,
    isShowingAllCategories,
    searchMatchCounts,
    collapsedGroups,
    toggleGroupCollapse,
  };
}
